import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Plus, Wrench, Users } from 'lucide-react';
import axios from 'axios';
import Toast from './assignment_management/Toast';
import ServiceRequestsList from './assignment_management/ServiceRequestsList';
import TechniciansList from './assignment_management/TechniciansList';
import AssignModal from './assignment_management/AssignModal';
import CreateRequestModal from './assignment_management/CreateRequestModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AssignmentManagement = () => {
  const { user, hasPermission } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);
  const [newRequestForm, setNewRequestForm] = useState({
    title: '',
    property: '',
    propertyName: '',
    description: '',
    priority: 'Medium',
    requiredSkills: [],
    estimatedTime: '',
    dueDate: ''
  });
  const [activeTab, setActiveTab] = useState('requests');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [toast, setToast] = useState(null);
  
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  
  // Sync pending data when coming back online
  const syncPendingData = async () => {
    try {
      const pendingRequests = JSON.parse(localStorage.getItem('pending_sync_service_requests') || '[]');
      const pendingAssignments = JSON.parse(localStorage.getItem('pending_sync_assignments') || '[]');
      
      // Sync pending service requests
      for (const request of pendingRequests) {
        try {
          const response = await fetch(`${API}/service-requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request)
          });
          
          if (response.ok) {
            console.log('Synced pending service request:', request.id);
          }
        } catch (error) {
          console.error('Failed to sync service request:', error);
        }
      }
      
      // Sync pending assignments
      for (const assignment of pendingAssignments) {
        try {
          const response = await fetch(`${API}/service-requests/${assignment.requestId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(assignment.data)
          });
          
          if (response.ok) {
            console.log('Synced pending assignment:', assignment.requestId);
          }
        } catch (error) {
          console.error('Failed to sync assignment:', error);
        }
      }
      
      // Clear pending sync data if all successful
      localStorage.removeItem('pending_sync_service_requests');
      localStorage.removeItem('pending_sync_assignments');
      
      if (pendingRequests.length > 0 || pendingAssignments.length > 0) {
        showToast('Data synchronized successfully', 'success');
      }
    } catch (error) {
      console.error('Error during sync:', error);
    }
  };

  // Get API configuration from localStorage
  const getApiConfig = () => {
    try {
      const token = localStorage.getItem('properties_api_token') || '';
      const endpoint = localStorage.getItem('properties_api_endpoint') || '';
      return { token, endpoint };
    } catch (error) {
      console.error('Error getting API config:', error);
      return { token: '', endpoint: '' };
    }
  };

  // Create axios instance with API token headers
  const createApiRequest = () => {
    const { token, endpoint } = getApiConfig();
    const headers = {};

    if (token) {
      headers['X-Properties-API-Token'] = token;
    }
    if (endpoint) {
      headers['X-Properties-API-Endpoint'] = endpoint;
    }

    return axios.create({
      baseURL: API,
      headers
    });
  };

  // Fetch properties from backend
  const fetchProperties = async () => {
    try {
      const apiClient = createApiRequest();
      const response = await apiClient.get('/properties/');
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setProperties([]);
    }
  };

  // Fetch actual technicians from the backend
  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/auth/users?role=Technician`, {
        withCredentials: true
      });
      
      // Transform backend user data to match component expectations
      const transformedTechnicians = response.data.map(user => ({
        id: user.id,
        name: `${user.name} ${user.surname}`,
        email: user.email,
        specialization: user.specialization || 'General Maintenance',
        availability: user.availability || 'Available',
        serviceArea: user.serviceArea || 'Not specified',
        experience: user.experience ? parseInt(user.experience) : 0,
        currentAssignments: 0, // This would need to be calculated from actual assignments
        rating: 4.5 // Default rating, would need to be calculated from actual reviews
      }));
      
      if (transformedTechnicians.length === 0) {
        console.log('No technicians found in backend. Using fallback mock data.');
        // Use fallback mock data if no technicians from backend
        setTechnicians(getMockTechnicians());
      } else {
        setTechnicians(transformedTechnicians);
      }
      
    } catch (error) {
      console.error('Error fetching technicians:', error);
      console.log('Backend unavailable. Using fallback mock technician data.');
      // Fallback to mock data instead of empty array
      setTechnicians(getMockTechnicians());
      showToast('Using offline technician data. Some features may be limited.', 'warning');
      
      if (error.response?.status === 403) {
        console.error('Insufficient permissions to fetch technicians');
      }
    }
  };

  // Mock technician data for fallback
  const getMockTechnicians = () => [
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@company.com',
      specialization: 'HVAC Systems',
      availability: 'Available',
      serviceArea: 'Building A-C',
      experience: 5,
      currentAssignments: 2,
      rating: 4.8
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@company.com',
      specialization: 'Electrical Systems',
      availability: 'Available',
      serviceArea: 'Building B-D',
      experience: 7,
      currentAssignments: 1,
      rating: 4.9
    },
    {
      id: 3,
      name: 'Mike Wilson',
      email: 'mike.wilson@company.com',
      specialization: 'Plumbing',
      availability: 'Available',
      serviceArea: 'All Buildings',
      experience: 3,
      currentAssignments: 0,
      rating: 4.6
    },
    {
      id: 4,
      name: 'Lisa Chen',
      email: 'lisa.chen@company.com',
      specialization: 'General Maintenance',
      availability: 'Busy',
      serviceArea: 'Building A-B',
      experience: 4,
      currentAssignments: 3,
      rating: 4.7
    }
  ];

  useEffect(() => {
    const fetchServiceRequests = async () => {
      setLoading(true);
      try {
        // Try to fetch from backend first
        const response = await fetch(`${API}/service-requests`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const serverRequests = await response.json();
          setServiceRequests(serverRequests);
          localStorage.setItem('service_requests', JSON.stringify(serverRequests));
        } else {
          throw new Error('Failed to fetch service requests');
        }
      } catch (error) {
        console.error('Error fetching service requests:', error);
        // Fallback to localStorage
        const storedRequests = localStorage.getItem('service_requests');
        if (storedRequests) {
          setServiceRequests(JSON.parse(storedRequests));
        } else {
          // Fallback to mock data if no stored data
          const mockRequests = [
            {
              id: 1,
              title: 'HVAC System Maintenance',
              property: 'Building A - Floor 3',
              description: 'Regular maintenance check for HVAC system',
              priority: 'High',
              status: 'Unassigned',
              requiredSkills: ['HVAC Systems'],
              estimatedTime: '2 hours',
              dueDate: '2024-01-20',
              createdDate: '2024-01-15'
            },
            {
              id: 2,
              title: 'Electrical Panel Inspection',
              property: 'Building B - Basement',
              description: 'Monthly electrical safety inspection',
              priority: 'Medium',
              status: 'Assigned',
              assignedTo: 'Sarah Johnson',
              requiredSkills: ['Electrical Systems'],
              estimatedTime: '3 hours',
              dueDate: '2024-01-25',
              createdDate: '2024-01-14'
            },
            {
              id: 3,
              title: 'Plumbing Repair',
              property: 'Building C - Unit 205',
              description: 'Fix leaking pipes in kitchen area',
              priority: 'High',
              status: 'Unassigned',
              requiredSkills: ['Plumbing'],
              estimatedTime: '1.5 hours',
              dueDate: '2024-01-18',
              createdDate: '2024-01-16'
            }
          ];
          setServiceRequests(mockRequests);
          localStorage.setItem('service_requests', JSON.stringify(mockRequests));
        }
      } finally {
        setLoading(false);
      }
    };

    // Fetch real technicians, properties, and service requests
    const loadData = async () => {
      try {
        await Promise.all([
          fetchTechnicians(),
          fetchProperties(),
          fetchServiceRequests()
        ]);
        
        // Double-check that technicians are loaded
        setTimeout(() => {
          if (technicians.length === 0) {
            console.log('Technicians still empty after fetch. Force loading mock data.');
            setTechnicians(getMockTechnicians());
          }
        }, 1000);
        
      } catch (error) {
        console.error('Error loading data:', error);
        // Ensure we have at least mock technicians
        setTechnicians(getMockTechnicians());
      }
    };
    
    loadData();
    
    // Add online/offline event listeners
    const handleOnline = () => {
      setIsOffline(false);
      showToast('Connection restored. Syncing data...', 'info');
      // Trigger sync of pending data
      syncPendingData();
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      showToast('Working offline. Changes will sync when connection is restored.', 'warning');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'Available': return 'text-green-600 bg-green-100';
      case 'Busy': return 'text-yellow-600 bg-yellow-100';
      case 'Unavailable': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Unassigned': return 'text-orange-600 bg-orange-100';
      case 'Assigned': return 'text-blue-600 bg-blue-100';
      case 'In Progress': return 'text-purple-600 bg-purple-100';
      case 'Completed': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleAssignTechnician = async () => {
    if (!selectedTechnician || !selectedRequest) {
      console.error('Missing selectedTechnician or selectedRequest:', { selectedTechnician, selectedRequest });
      return;
    }

    try {
      // Enhanced debug logging
      console.log('=== ASSIGNMENT DEBUG START ===');
      console.log('Selected technician ID:', selectedTechnician, typeof selectedTechnician);
      console.log('Technicians array length:', technicians.length);
      console.log('Available technicians:', technicians.map(t => ({ id: t.id, name: t.name, idType: typeof t.id })));
      
      // Ensure technicians are loaded
      if (technicians.length === 0) {
        console.error('No technicians available. Loading mock data...');
        const mockTechs = getMockTechnicians();
        setTechnicians(mockTechs);
        showToast('Loading technician data...', 'info');
        return;
      }
      
      // More robust ID matching - handle both string and number IDs
      const assignedTechnician = technicians.find(t => 
        t.id === selectedTechnician || 
        t.id === parseInt(selectedTechnician) || 
        String(t.id) === String(selectedTechnician)
      );
      
      const assignedTechnicianName = assignedTechnician?.name;
      
      console.log('Found technician:', assignedTechnician);
      console.log('Assigned technician name:', assignedTechnicianName);
      console.log('=== ASSIGNMENT DEBUG END ===');
      
      if (!assignedTechnicianName) {
        console.error('Could not find technician with ID:', selectedTechnician);
        console.error('Available technician IDs:', technicians.map(t => t.id));
        showToast('Error: Could not find selected technician', 'error');
        return;
      }
      
      // Update the service request status
      const updatedRequests = serviceRequests.map(request => 
        request.id === selectedRequest.id 
          ? { 
              ...request, 
              status: 'Assigned', 
              assignedTo: assignedTechnicianName,
              updated_at: new Date().toISOString()
            }
          : request
      );
      
      setServiceRequests(updatedRequests);
      localStorage.setItem('service_requests', JSON.stringify(updatedRequests));

      // Update technician's current assignments
      setTechnicians(prev => prev.map(tech => 
        tech.id === parseInt(selectedTechnician)
          ? { ...tech, currentAssignments: tech.currentAssignments + 1 }
          : tech
      ));

      // Try to sync with backend if online
      if (navigator.onLine) {
        try {
          const response = await fetch(`${API}/service-requests/${selectedRequest.id}/assign`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              technician_id: selectedTechnician,
              technician_name: assignedTechnicianName
            })
          });

          if (response.ok) {
            console.log('Technician assigned successfully');
            showToast('Technician assigned successfully', 'success');
          } else {
            console.log('Assignment saved locally. API sync failed.');
            showToast('Assignment saved locally. API sync failed.', 'warning');
          }
        } catch (error) {
          console.error('Error syncing assignment:', error);
          console.log('Assignment saved locally. Will sync when online.');
          showToast('Assignment saved locally. Will sync when online.', 'warning');
        }
      } else {
        console.log('Assignment saved locally. Will sync when online.');
        showToast('Assignment saved locally. Will sync when online.', 'warning');
      }

      setShowAssignModal(false);
      setSelectedRequest(null);
      setSelectedTechnician('');
      
    } catch (error) {
      console.error('Error assigning technician:', error);
      showToast('Failed to assign technician', 'error');
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    
    if (!newRequestForm.title || !newRequestForm.property || !newRequestForm.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const newRequest = {
        id: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        ...newRequestForm,
        status: 'Unassigned',
        createdDate: new Date().toISOString().split('T')[0],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        requiredSkills: newRequestForm.requiredSkills.length > 0 ? newRequestForm.requiredSkills : ['General Maintenance']
      };

      // Update local state and localStorage immediately
      const currentRequests = [...serviceRequests];
      const updatedRequests = [newRequest, ...currentRequests];
      setServiceRequests(updatedRequests);
      localStorage.setItem('service_requests', JSON.stringify(updatedRequests));

      // Try to sync with backend if online
      if (navigator.onLine) {
        try {
          const apiData = {
            title: newRequest.title,
            property_id: newRequest.property,
            property_name: newRequest.propertyName,
            description: newRequest.description,
            priority: newRequest.priority,
            required_skills: newRequest.requiredSkills,
            estimated_time: newRequest.estimatedTime,
            due_date: newRequest.dueDate,
            special_instructions: newRequest.specialInstructions || '',
            status: newRequest.status
          };

          const response = await fetch(`${API}/service-requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(apiData)
          });

          if (response.ok) {
            const apiResponse = await response.json();
            
            // Update with server-generated ID if different
            if (apiResponse.id && apiResponse.id !== newRequest.id) {
              const updatedRequest = { ...newRequest, id: apiResponse.id };
              const finalRequests = [updatedRequest, ...currentRequests];
              setServiceRequests(finalRequests);
              localStorage.setItem('service_requests', JSON.stringify(finalRequests));
            }
            
            console.log('Service request created successfully');
            showToast('Service request created successfully', 'success');
          } else {
            console.log('Service request saved locally. API sync failed.');
            showToast('Service request saved locally. API sync failed.', 'warning');
            // Store for later sync
            const pendingSync = JSON.parse(localStorage.getItem('pending_sync_service_requests') || '[]');
            pendingSync.push(newRequest);
            localStorage.setItem('pending_sync_service_requests', JSON.stringify(pendingSync));
          }
        } catch (error) {
           console.error('Error syncing with backend:', error);
           console.log('Service request saved locally. Will sync when online.');
           showToast('Service request saved locally. Will sync when online.', 'warning');
          // Store for later sync
          const pendingSync = JSON.parse(localStorage.getItem('pending_sync_service_requests') || '[]');
          pendingSync.push(newRequest);
          localStorage.setItem('pending_sync_service_requests', JSON.stringify(pendingSync));
        }
      } else {
        console.log('Service request saved locally. Will sync when online.');
        // Store for later sync
        const pendingSync = JSON.parse(localStorage.getItem('pending_sync_service_requests') || '[]');
        pendingSync.push(newRequest);
        localStorage.setItem('pending_sync_service_requests', JSON.stringify(pendingSync));
      }

      // Reset form and close modal
      setShowCreateRequestModal(false);
      setNewRequestForm({
        title: '',
        property: '',
        propertyName: '',
        description: '',
        priority: 'Medium',
        requiredSkills: [],
        estimatedTime: '',
        dueDate: '',
        specialInstructions: ''
      });
      
    } catch (error) {
      console.error('Error creating service request:', error);
      showToast('Failed to create service request', 'error');
    }
  };

  const handleSkillToggle = (skill) => {
    setNewRequestForm(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.includes(skill)
        ? prev.requiredSkills.filter(s => s !== skill)
        : [...prev.requiredSkills, skill]
    }));
  };

  const availableSkills = ['HVAC Systems', 'Electrical Systems', 'Plumbing', 'General Maintenance', 'Security Systems', 'Fire Safety', 'Carpentry', 'Painting'];

  const getRecommendedTechnicians = (request) => {
    // Add safety checks to prevent JavaScript errors
    if (!request || !Array.isArray(request.requiredSkills) || request.requiredSkills.length === 0) {
      return technicians.filter(tech => tech.availability === 'Available');
    }
    
    return technicians
      .filter(tech => 
        tech.availability === 'Available' &&
        tech.specialization &&
        request.requiredSkills.some(skill => 
          typeof skill === 'string' && 
          typeof tech.specialization === 'string' &&
          tech.specialization.includes(skill)
        )
      )
      .sort((a, b) => {
        // Sort by rating and current workload
        const scoreA = (a.rating || 0) - ((a.currentAssignments || 0) * 0.1);
        const scoreB = (b.rating || 0) - ((b.currentAssignments || 0) * 0.1);
        return scoreB - scoreA;
      });
  };

  const filteredRequests = serviceRequests.filter(request => {
    // Add safety checks to prevent JavaScript errors
    if (!request || typeof request !== 'object') return false;
    
    const title = request.title || '';
    const property = request.property || '';
    const status = request.status || '';
    const search = searchTerm || '';
    
    const matchesSearch = title.toLowerCase().includes(search.toLowerCase()) ||
                         property.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterStatus === 'all' || status.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Assignment Management
        </h1>
        <p className="text-gray-600">
          Manage technician assignments and service requests
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('requests')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Wrench className="inline-block w-4 h-4 mr-2" />
              Service Requests
            </button>
            <button
              onClick={() => setActiveTab('technicians')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'technicians'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="inline-block w-4 h-4 mr-2" />
              Technicians
            </button>
          </nav>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={activeTab === 'requests' ? 'Search requests...' : 'Search technicians...'}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {activeTab === 'requests' && (
            <>
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="unassigned">Unassigned</option>
                <option value="assigned">Assigned</option>
                <option value="in progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <button
                onClick={() => setShowCreateRequestModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus size={16} />
                New Request
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'requests' && (
        <ServiceRequestsList
          filteredRequests={filteredRequests}
          getPriorityColor={getPriorityColor}
          getStatusColor={getStatusColor}
          getRecommendedTechnicians={getRecommendedTechnicians}
          setSelectedRequest={setSelectedRequest}
          setShowAssignModal={setShowAssignModal}
        />
      )}

      {activeTab === 'technicians' && (
        <TechniciansList
          technicians={technicians}
          serviceRequests={serviceRequests}
          searchTerm={searchTerm}
          getAvailabilityColor={getAvailabilityColor}
          getStatusColor={getStatusColor}
        />
      )}

      {/* Assignment Modal */}
      {showAssignModal && selectedRequest && (
        <AssignModal
          selectedRequest={selectedRequest}
          technicians={technicians}
          selectedTechnician={selectedTechnician}
          setSelectedTechnician={setSelectedTechnician}
          handleAssignTechnician={handleAssignTechnician}
          setShowAssignModal={setShowAssignModal}
          setSelectedRequest={setSelectedRequest}
          getMockTechnicians={getMockTechnicians}
          setTechnicians={setTechnicians}
          showToast={showToast}
        />
      )}

      {/* Create Request Modal */}
      {showCreateRequestModal && (
        <CreateRequestModal
          newRequestForm={newRequestForm}
          setNewRequestForm={setNewRequestForm}
          handleCreateRequest={handleCreateRequest}
          handleSkillToggle={handleSkillToggle}
          availableSkills={availableSkills}
          properties={properties}
          setShowCreateRequestModal={setShowCreateRequestModal}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AssignmentManagement;